import { memo } from 'react';

import type { DrilledHole } from '~api/services';
import type { ColourScale } from '~utils';
import type { Coords } from './DrillHolesLayerBase';
import { buildCoords, DrillHolesLayerBase } from './DrillHolesLayerBase';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type DrilledDrillHolesLayerProps = {
  /** Drilled holes to render */
  drillHoles: DrilledHole[];
  /** Optional colour scale to use instead of creating a continuous scale */
  colourScale?: ColourScale;
  /** Currently selected hole identifier (hole_name) */
  selectedHoleId: string | null;
  /** Called when a hole is activated/selected */
  onHoleSelect: (hole: DrilledHole) => void;
  /** Optional formatter for the title attribute of each hole */
  titleFormatter?: (hole: DrilledHole) => string;
  /** Whether to render planned or actual coordinates */
  plannedOrActualCoordinates: 'planned' | 'actual';
};

/* -------------------------------------------------------------------------- */
/*                         Module-level selector functions                    */
/* -------------------------------------------------------------------------- */

const getPlannedCoords = (hole: DrilledHole): Coords | null => buildCoords(hole.collarx_planned, hole.collary_planned);

const getActualCoords = (hole: DrilledHole): Coords | null => buildCoords(hole.collarx_actual, hole.collary_actual);

const getPropertyValue = (hole: DrilledHole, property: string): unknown => hole[property as keyof DrilledHole] ?? null;

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Adapter layer that renders `DrilledHole` records as SVG circles.
 * Wraps `DrillHolesLayerBase` with typed selectors for the DrilledHole schema.
 */
const DrilledDrillHolesLayerInner = ({
  drillHoles,
  colourScale,
  selectedHoleId,
  onHoleSelect,
  titleFormatter,
  plannedOrActualCoordinates,
}: DrilledDrillHolesLayerProps) => (
  <DrillHolesLayerBase
    drillHoles={drillHoles}
    getPlannedCoords={getPlannedCoords}
    getActualCoords={getActualCoords}
    getPropertyValue={getPropertyValue}
    colourScale={colourScale}
    selectedHoleId={selectedHoleId}
    onHoleSelect={onHoleSelect}
    titleFormatter={titleFormatter}
    plannedOrActualCoordinates={plannedOrActualCoordinates}
    type="drilled"
  />
);

const DrilledDrillHolesLayer = memo(DrilledDrillHolesLayerInner);
DrilledDrillHolesLayer.displayName = 'DrilledDrillHolesLayer';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { DrilledDrillHolesLayer, type DrilledDrillHolesLayerProps };
